import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { Chip, Empty } from "@/components/ui-bits";
import { useBrand, Branded, BrandLogo, PoweredBy, brandName } from "@/components/Brand";
import { MembershipPause, PauseChip } from "@/components/MembershipPause";

const money = (a, c) => `${(c || "aud").toUpperCase()} ${Number(a).toFixed(2)}`;

export const ClientPayments = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/client/me/payments").then((r) => setD(r.data)); }, []);
  const pay = async (inv) => {
    setBusy(inv.id);
    try { const r = await api.post("/payments/checkout", { invoiceId: inv.id, origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  if (!d) return <div className="center"><span className="spin" /></div>;
  const open = d.invoices.filter((i) => i.status === "sent");
  return (
    <div className="fade" data-testid="client-payments">
      <div className="grid2">
        <div className="panel">
          <div className="ph"><h3>Your invoices</h3><span className="chip gray num" data-testid="client-credits">{d.credits.remaining} session{d.credits.remaining !== 1 ? "s" : ""} left</span></div>
          {open.length > 0 && <div className="msg" data-testid="client-invoices-due"><div className="who"><span>Action needed</span></div><div className="txt">{open.length} invoice{open.length !== 1 ? "s" : ""} waiting for payment.</div></div>}
          {d.invoices.length === 0 && <Empty>No invoices yet.</Empty>}
          {d.invoices.map((inv) => (
            <div className="sess" key={inv.id} data-testid={`client-invoice-${inv.id}`}>
              <div className="sb"><div className="st">{inv.number} · {inv.packName}{inv.recurring && <span className="chip gray" style={{ marginLeft: 6 }}>membership</span>}</div><div className="ss">{inv.sessions} sessions · {inv.status === "paid" ? `paid ${fmt(inv.paidAt, { day: "numeric", month: "short" })}` : `due ${fmt(inv.dueAt, { day: "numeric", month: "short" })}`}{inv.note ? ` · “${inv.note}”` : ""}</div></div>
              <b className="num">{money(inv.amount, inv.currency)}</b>
              <Chip cls={inv.status === "paid" ? "ok" : inv.status === "sent" ? "warn" : "gray"} label={inv.status === "sent" ? "unpaid" : inv.status} />
              <PauseChip inv={inv} />
              {inv.status === "sent" && <button className="btn sm" disabled={busy === inv.id} onClick={() => pay(inv)} data-testid={`pay-invoice-${inv.id}`}>{busy === inv.id ? "Opening…" : "Pay now"}</button>}
              <MembershipPause inv={inv} maxWeeks={d.maxPauseWeeks} asClient onChange={(u) => setD({ ...d, invoices: d.invoices.map((x) => (x.id === u.id ? { ...x, ...u } : x)) })} />
            </div>
          ))}
          {d.invoices.some((i) => i.subscriptionId && i.status === "paid") && <div className="ss" style={{ marginTop: 8 }} data-testid="client-pause-hint">Going away? Pause your membership for up to {d.maxPauseWeeks} weeks — no charges until it resumes, nothing to cancel.</div>}
        </div>
        <div className="panel">
          <h3>Session packs</h3>
          <p className="ss" style={{ marginTop: 0 }}>Ask your coach for an invoice on any pack — it arrives here with a secure pay link.</p>
          {d.packs.length === 0 && <Empty>No packs on offer yet.</Empty>}
          {d.packs.map((p) => <div className="excard" key={p.id} data-testid={`client-pack-${p.id}`}><div className="ph" style={{ marginBottom: 2 }}><div className="st">{p.name} <span className="ss">· {p.sessions} sessions{p.recurring ? "/month · renews" : ""}</span></div><b className="num">{money(p.price, p.currency)}</b></div>{p.description && <div className="ss">{p.description}</div>}</div>)}
          {d.credits.bought > 0 && d.credits.remaining <= 1 && <div className="msg" data-testid="client-credits-low"><div className="who"><span>{d.credits.remaining === 0 ? "Pack used up" : "1 session left"}</span></div><div className="txt">Ask your coach for the next invoice so there's no gap in your training.</div></div>}
          <div className="kv" style={{ marginTop: 10 }}><span>Sessions bought</span><span className="num">{d.credits.bought}</span></div>
          <div className="kv"><span>Sessions used</span><span className="num">{d.credits.used}</span></div>
        </div>
      </div>
    </div>
  );
};

export default function PaymentResult({ ok }) {
  const brand = useBrand();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState(ok ? "checking" : "cancelled");
  useEffect(() => {
    if (!ok || !sid) return;
    let tries = 0;
    const poll = async () => {
      try { const r = await api.get(`/payments/status/${sid}`); if (r.data.payment_status === "paid") return setState("paid"); if (["expired", "failed"].includes(r.data.payment_status)) return setState("failed"); }
      catch { /* retry */ }
      if (++tries < 6) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
  }, [ok, sid]);
  return (
    <Branded brand={brand}>
      <div className="login fade" style={{ maxWidth: 480 }} data-testid="payment-result">
        <div className="brand" style={{ marginBottom: 16 }}><BrandLogo brand={brand} size={34} /><div><b style={{ fontSize: 16 }}>{brandName(brand).toUpperCase()}</b><small>PAYMENT</small></div></div>
        {state === "checking" && <><h1>CONFIRMING…</h1><p className="ss"><span className="spin" /> Checking with Stripe — a few seconds.</p></>}
        {state === "paid" && <><h1>PAID ✓</h1><p data-testid="payment-paid">Thank you — your sessions have been added. A receipt is on its way.</p></>}
        {state === "pending" && <><h1>ALMOST THERE</h1><p data-testid="payment-pending">Your payment is still processing. We'll confirm by message as soon as it lands.</p></>}
        {state === "failed" && <><h1>NOT COMPLETED</h1><p data-testid="payment-failed">That checkout expired or failed. You can try again from your Payments tab.</p></>}
        {state === "cancelled" && <><h1>CHECKOUT CANCELLED</h1><p data-testid="payment-cancelled">No charge was made. Your invoice is still waiting whenever you're ready.</p></>}
        <Link to="/client#payments" className="btn" style={{ display: "inline-block", marginTop: 14 }} data-testid="payment-back">Back to my payments</Link>
        <PoweredBy />
      </div>
    </Branded>
  );
}
